import type { Route, Alert } from '@/types';

function isRail(route: Route): boolean {
  const mode = route.mode_name?.toLowerCase();
  return mode === 'light rail' || mode === 'subway';
}

/**
 * Determines if black text should be used based on route properties
 * Light Rail and Subway use their route color for text
 */
export function useBlackText(route: Route): boolean {
  if (isRail(route)) {
    return false;
  }
  return route.route_text_color === '000000';
}

/**
 * Gets the cell style for a route card
 */
export function getCellStyle(route: Route): React.CSSProperties {
  return {
    background: `#${route.route_color}`,
    color: `#${route.route_text_color}`,
  };
}

/**
 * Gets the appropriate image size class based on route display name
 */
export function getImageSize(route: Route): number {
  if (route.route_display_short_name) {
    return route.route_display_short_name.elements[1] ? 28 : 34;
  }
  return 0;
}

/**
 * Gets the image URL for a route icon
 */
export function getImageUrl(route: Route, index: number): string | null {
  if (
    route.route_display_short_name &&
    route.route_display_short_name.elements[index]
  ) {
    const hex = useBlackText(route) ? '000000' : route.route_color;
    return `/api/images/${route.route_display_short_name.elements[index]}.svg?primaryColor=${hex}`;
  }
  return null;
}

/**
 * Gets the pin URL for a route marker
 */
export function getPinUrl(route: Route): string {
  const hex = useBlackText(route) ? '' : `${route.route_color}/`;
  return `https://widget.transitapp.com/images/28/${hex}pin.png`;
}

/**
 * Formats the route name for display
 */
export function getRouteName(route: Route): string {
  if (isRail(route)) {
    return route.route_long_name || `${route.route_short_name} Line`;
  }

  // For BRT routes with non-numeric short names (e.g. HealthLine "HL"), use the long name
  // API returns "Healthline" but the official branding is "HealthLine"
  if (route.mode_name?.toLowerCase() === 'brt' && !/^\d+$/.test(route.route_short_name) && route.route_long_name) {
    if (route.route_long_name.toLowerCase() === 'healthline') {
      return 'HealthLine';
    }
    return route.route_long_name;
  }

  // Use elements[1] if available, otherwise route_short_name
  const name =
    route.route_display_short_name?.elements[1] || route.route_short_name;

  // Prepend # if the name is a number
  if (/^\d+$/.test(name)) {
    return `#${name}`;
  }

  return name;
}

/**
 * Checks if a route has alerts
 */
export function hasAlerts(route: Route): boolean {
  return Boolean(route.alerts && route.alerts.length > 0);
}

/**
 * Checks if a route has a severe alert
 */
export function hasSevereAlert(route: Route): boolean {
  return Boolean(
    route.alerts?.some((alert) => alert.severity === 'Severe')
  );
}

/**
 * Gets alerts sorted by severity (Severe > Warning > Info)
 */
export function getSortedAlerts(route: Route): Alert[] {
  if (!route.alerts) return [];

  const severityOrder: Record<string, number> = {
    Severe: 0,
    Warning: 1,
    Info: 2,
  };

  return [...route.alerts].sort((a, b) => {
    const orderA = severityOrder[a.severity] ?? 99;
    const orderB = severityOrder[b.severity] ?? 99;
    return orderA - orderB;
  });
}
